/**
 * Deep-clone a value and assign new `aitid` values where present.
 *
 * This performs a recursive clone of objects and arrays. When an object has
 * an `aitid` property the clone will receive a fresh `aitid` (via
 * `crypto.randomUUID()`); `templateAitId` and `templateColumnAitId` keys are
 * removed. If `coreTrafficLight` is present it is set to `ChangedUnknown` on
 * the clone.
 *
 * @template T - The type of the value to duplicate
 * @param obj - The value to clone; primitives are returned unchanged
 * @returns A deep-cloned value with new `aitid` values and adjusted template/traffic-light fields
 */
export const trDuplicate = <T>(obj: T): T => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  } else if (Array.isArray(obj)) {
    return obj.map((item) => trDuplicate(item)) as T;
  } else {
    const newObj: Record<string, unknown> = {};
    for (const key in obj) {
      /* istanbul ignore else */
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = trDuplicate((obj as Record<string, unknown>)[key]);
      }
    }
    // Assign new aitid
    if ("aitid" in newObj) newObj.aitid = crypto.randomUUID();
    if ("coreTrafficLight" in newObj) newObj.coreTrafficLight = "ChangedUnknown";

    // Remove template AitIds when duplicating
    if ("templateAitId" in newObj) delete newObj.templateAitId;
    if ("templateColumnAitId" in newObj) delete newObj.templateColumnAitId;

    return newObj as T;
  }
};
